import React, { useMemo, useEffect, useCallback, useState } from "react";
import { observer } from "mobx-react-lite";

import "./DamagesVideo.scss";
import VideoPlayer from "../../../components/video-player/VideoPlayer";
import { translationService } from "../../../services/translation/TranslationService";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import useSessionStorage from "../../../services/storage/SessionStorageService";
import useTheme from "../../../services/theme-service/ThemeService";
import LoadingSpinner from "../../../components/loading-spinner/LoadingSpinner";
import Svg from "../../../components/svg/Svg";
import { Warning } from "../../../components/svg/svgImageList";
import { InspectionFilesStorageModel } from "../../../services/inspection/InspectionFilesStorage";
import { api } from "../../../services/api/ApiService";
import {
  RunVideoConversion,
  RunVideoConversionFromAssignedProject,
} from "../../../services/authorization/Permissions";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import { CurrentInspectionStorage } from "../../../services/inspection/CurrentInspectionStorage";
import useUrlParams from "../../../services/url/UrlParamsService";
import { videoPlayerService } from "../../../services/video-player/VideoPlayerService";
import { DamagesStorage } from "../../../services/damage/DamagesStorage";
import { VideoAnalysisService } from "../../../services/inspection/VideoAnalysisService";

interface DamagesVideoProps {
  currentInspectionStorage: CurrentInspectionStorage;
  damagesStorage: DamagesStorage;
  videoAnalysis?: VideoAnalysisService;
}

export default observer(function DamagesVideo(props: DamagesVideoProps) {
  const { currentInspectionStorage, damagesStorage } = props;

  const urlParamsService = useUrlParams();
  const { themeClass } = useTheme();
  const { setStorageItem, getStorageItem } = useSessionStorage();
  const { loadVideo, loadVideoTimestamps, loadVideoWithoutReset, videoUrl, aVideoIsLoaded } =
    videoPlayerService;
  const { handleErrorInSnackbar } = useErrorHandler();
  const currentInspectionId = urlParamsService.getInspectionId()!;

  const [inspectionFilesStorage] = useState(() =>
    InspectionFilesStorageModel.create({ uploadService: { isUploadInProgress: false } }, { api })
  );

  const [conversionRetryLoading, setConversionRetryLoading] = useState(false);

  const inspectionVideoIdFromSession = useMemo(() => {
    var inspectionVideoId = getStorageItem("inspectionVideoId");
    return parseInt(inspectionVideoId);
  }, [getStorageItem]);

  useEffect(() => {
    loadVideoTimestamps(damagesStorage.videoPlayerTimestamps);

    if (currentInspectionId === inspectionVideoIdFromSession && aVideoIsLoaded) {
      loadVideoWithoutReset(videoUrl);
    } else {
      loadVideo(currentInspectionStorage.currentInspection.videoURL);
    }
  }, [aVideoIsLoaded, currentInspectionId, currentInspectionStorage.currentInspection.videoURL, damagesStorage.videoPlayerTimestamps, inspectionVideoIdFromSession, loadVideo, loadVideoTimestamps, loadVideoWithoutReset, videoUrl]);

  const handleRetryConversion = useCallback(async () => {
    if (currentInspectionStorage.currentInspection.video) {
      try {
        setConversionRetryLoading(true);
        await inspectionFilesStorage.convertVideo(
          currentInspectionStorage.currentInspection.video.id
        );
        await currentInspectionStorage.reloadCurrentInspection();
      } catch (error) {
        const applicationError = error as ApplicationError;
        handleErrorInSnackbar(applicationError);
      }
    }
  }, [currentInspectionStorage, inspectionFilesStorage, handleErrorInSnackbar]);

  useEffect(() => {
    setStorageItem("inspectionVideoId", currentInspectionId.toString());
  }, [setStorageItem, currentInspectionId]);

  useEffect(() => {
    if (!currentInspectionStorage.isLoading) {
      setConversionRetryLoading(false);
    }
  }, [currentInspectionStorage.isLoading]);

  return (
    <>
      {(conversionRetryLoading ||
        currentInspectionStorage.currentInspection.video?.isConverting) && (
        <div className={themeClass(["video-not-available"])}>
          <LoadingSpinner spinnerOnly />
          <p className={themeClass(["message"])}>
            {translationService.translate("damages.DamagesVideo.video-conversion-format-message")}
          </p>
          <p className={themeClass(["message"])}>
            {translationService.translate("damages.DamagesVideo.video-conversion-wait-message")}
          </p>
        </div>
      )}
      {currentInspectionStorage.currentInspection.video?.conversionFailed &&
        !conversionRetryLoading && (
          <div className={themeClass(["video-failed-conversion"])}>
            <div className="svg-warning">
              <Svg svg={Warning} width={100} />
            </div>
            {permissionsService.userHasOneOfThePermissions([
              RunVideoConversion,
              RunVideoConversionFromAssignedProject,
            ]) && (
              <>
                <p className={themeClass(["message"])}>
                  {` ${translationService.translate(
                    "damages.DamagesVideo.video-conversion-failed-message"
                  )} `}
                  <span onClick={handleRetryConversion}>
                    {translationService.translate(
                      "damages.DamagesVideo.video-conversion-error-warning-link"
                    )}
                  </span>
                </p>
                <p className={themeClass(["message"])}>
                  {` ${translationService.translate(
                    "damages.DamagesVideo.video-conversion-error-warning-contact"
                  )} `}
                </p>
              </>
            )}
            {!permissionsService.userHasOneOfThePermissions([
              RunVideoConversion,
              RunVideoConversionFromAssignedProject,
            ]) && (
              <>
                <p className={themeClass(["message"])}>
                  {` ${translationService.translate(
                    "damages.DamagesVideo.video-conversion-failed-message"
                  )} `}
                </p>
                <p className={themeClass(["message"])}>
                  {` ${translationService.translate(
                    "damages.DamagesVideo.video-conversion-error-warning-for-guests"
                  )} `}
                </p>
              </>
            )}
            <p className={themeClass(["message", "margin"])}>
              {currentInspectionStorage.currentInspection.video.conversionErrorMessage}
            </p>
          </div>
        )}
      {currentInspectionStorage.currentInspection.hasVideo &&
        currentInspectionStorage.currentInspection.video?.isAvailable && (
          <VideoPlayer videoAnalysis={props.videoAnalysis} />
        )}
    </>
  );
});
